import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  TrendingUp, DollarSign, ShoppingBag, PieChart,
  BarChart3, ChefHat, Award, Receipt, ArrowRight,
  ShieldAlert, Activity, FileSpreadsheet, ChevronDown, ChevronUp,
  Coins, Sparkles
} from 'lucide-react';
import { useKpiHealth, useKpiOverview } from '../hooks/useKpi';
import { PeriodSelector, PeriodType } from '../components/PeriodSelector';
import { KpiCard } from '../components/KpiCard';
import { TrendAreaChart } from '../components/TrendAreaChart';
import { DonutChart } from '../components/DonutChart';
import { TopProductsTable } from '../components/TopProductsTable';
import { HourHeatmap } from '../components/HourHeatmap';
import { OrderCycleFunnel } from '../components/OrderCycleFunnel';
import { InsightsBanner } from '../components/InsightsBanner';
import { formatCOP, formatCOPShort, formatNumber, formatPct } from '../utils/kpiFormatters';

export default function KpiDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Estado de filtros de periodo
  const periodParam = (searchParams.get('period') as PeriodType) || '7d';
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>(periodParam);
  const [customFrom, setCustomFrom] = useState(searchParams.get('from') || '');
  const [customTo, setCustomTo] = useState(searchParams.get('to') || '');
  const [isComparing, setIsComparing] = useState(true);
  const [trendMetric, setTrendMetric] = useState<'revenue' | 'orders'>('revenue');
  const [showAdvancedExplorer, setShowAdvancedExplorer] = useState(false);

  // Consulta de datos al backend
  const health = useKpiHealth();
  const overviewQuery = useKpiOverview({
    period: selectedPeriod,
    from: customFrom || undefined,
    to: customTo || undefined,
  });

  const online = health.data?.status === 'ok';
  const data = overviewQuery.data;
  const kpis = data?.kpis;

  const handlePeriodChange = (newPeriod: PeriodType, from?: string, to?: string) => {
    setSelectedPeriod(newPeriod);
    const params = new URLSearchParams(searchParams);
    params.set('period', newPeriod);
    if (from && to) {
      setCustomFrom(from);
      setCustomTo(to);
      params.set('from', from);
      params.set('to', to);
    } else {
      setCustomFrom('');
      setCustomTo('');
      params.delete('from');
      params.delete('to');
    }
    setSearchParams(params, { replace: true });
  };

  return (
    <div className="space-y-6 pb-12">
      {/* ── 1. Encabezado Principal ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-black text-text tracking-tight">KPIs & Tablero de Negocio</h1>
            <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 text-[10px] font-black uppercase tracking-wider">
              <Sparkles size={11} /> Ejecutivo
            </span>
          </div>
          <p className="mt-1 text-sm text-text-muted">
            Indicadores financieros, rentabilidad de recetas y rendimiento operativo en tiempo real
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold ${
              online ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-rose-50 text-rose-700'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${online ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`}
            />
            {online ? 'Servicio analítico operativo' : 'Desconectado'}
          </span>
        </div>
      </div>

      {/* ── 2. Selector de Periodo y Comparativa ── */}
      <PeriodSelector
        selectedPeriod={selectedPeriod}
        onSelectPeriod={handlePeriodChange}
        customFrom={customFrom}
        customTo={customTo}
        isComparing={isComparing}
        onToggleCompare={setIsComparing}
        onRefresh={() => overviewQuery.refetch()}
        isRefreshing={overviewQuery.isFetching}
      />

      {/* ── 3. Insights Inteligentes Automáticos ── */}
      {data?.insights && data.insights.length > 0 && (
        <InsightsBanner insights={data.insights} />
      )}

      {/* ── 4. Tarjetas de KPIs Principales ── */}
      {overviewQuery.isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-2xl bg-muted" />
          ))}
        </div>
      ) : kpis ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {/* 1. Ventas Netas */}
          <KpiCard
            title={kpis.revenue.label}
            value={formatCOP(kpis.revenue.value)}
            icon={<DollarSign size={16} />}
            iconBg="bg-emerald-50 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400"
            description={kpis.revenue.description}
            deltaPct={isComparing ? kpis.revenue.delta_pct : undefined}
            previousValueFormatted={formatCOPShort(kpis.revenue.previous)}
          />

          {/* 2. Pedidos Pagados */}
          <KpiCard
            title={kpis.orders.label}
            value={formatNumber(kpis.orders.value)}
            icon={<ShoppingBag size={16} />}
            iconBg="bg-sky-50 text-sky-600 dark:bg-sky-950 dark:text-sky-400"
            description={kpis.orders.description}
            deltaPct={isComparing ? kpis.orders.delta_pct : undefined}
            previousValueFormatted={`${formatNumber(kpis.orders.previous)} ord.`}
          />

          {/* 3. Ticket Promedio */}
          <KpiCard
            title={kpis.avg_ticket.label}
            value={formatCOP(kpis.avg_ticket.value)}
            icon={<TrendingUp size={16} />}
            iconBg="bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-400"
            description={kpis.avg_ticket.description}
            deltaPct={isComparing ? kpis.avg_ticket.delta_pct : undefined}
            previousValueFormatted={formatCOPShort(kpis.avg_ticket.previous)}
          />

          {/* 4. Utilidad Bruta */}
          <KpiCard
            title={kpis.gross_profit.label}
            value={formatCOP(kpis.gross_profit.value)}
            icon={<Coins size={16} />}
            iconBg="bg-amber-50 text-amber-600 dark:bg-amber-950 dark:text-amber-400"
            description={kpis.gross_profit.description}
            deltaPct={isComparing ? kpis.gross_profit.delta_pct : undefined}
            previousValueFormatted={formatCOPShort(kpis.gross_profit.previous)}
            subtitle={`Margen: ${kpis.gross_margin_pct.value}%`}
          />

          {/* 5. Food Cost % (Insumos) */}
          <KpiCard
            title={kpis.food_cost_pct.label}
            value={formatPct(kpis.food_cost_pct.value, false)}
            icon={<ChefHat size={16} />}
            iconBg="bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400"
            description={kpis.food_cost_pct.description}
            deltaPct={isComparing ? kpis.food_cost_pct.delta_pts : undefined}
            invertDeltaColor={true}
            previousValueFormatted={`${kpis.food_cost_pct.previous}%`}
            statusBadge={{
              text: `Meta ${kpis.food_cost_pct.target}%`,
              variant: kpis.food_cost_pct.status || 'optimal',
            }}
          />

          {/* 6. Gastos Operativos & Compras */}
          <KpiCard
            title={kpis.expenses.label}
            value={formatCOP(kpis.expenses.value)}
            icon={<Receipt size={16} />}
            iconBg="bg-rose-50 text-rose-600 dark:bg-rose-950 dark:text-rose-400"
            description={kpis.expenses.description}
            deltaPct={isComparing ? kpis.expenses.delta_pct : undefined}
            invertDeltaColor={true}
            previousValueFormatted={formatCOPShort(kpis.expenses.previous)}
          />

          {/* 7. Utilidad Neta Estimada */}
          <KpiCard
            title={kpis.net_profit.label}
            value={formatCOP(kpis.net_profit.value)}
            icon={<DollarSign size={16} />}
            iconBg={kpis.net_profit.value >= 0 ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-950' : 'bg-rose-50 text-rose-600'}
            description={kpis.net_profit.description}
            deltaPct={isComparing ? kpis.net_profit.delta_pct : undefined}
            previousValueFormatted={formatCOPShort(kpis.net_profit.previous)}
          />

          {/* 8. Propinas Recaudadas */}
          <KpiCard
            title={kpis.tips.label}
            value={formatCOP(kpis.tips.value)}
            icon={<Award size={16} />}
            iconBg="bg-teal-50 text-teal-600 dark:bg-teal-950 dark:text-teal-400"
            description={kpis.tips.description}
            deltaPct={isComparing ? kpis.tips.delta_pct : undefined}
            previousValueFormatted={formatCOPShort(kpis.tips.previous)}
          />
        </div>
      ) : null}

      {/* ── 5. Gráfica de Tendencia Temporal ── */}
      <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-base font-bold text-text">Evolución de Ventas en el Tiempo</h2>
            <p className="text-xs text-text-muted">
              Comparativa del periodo seleccionado frente al periodo anterior equivalente
            </p>
          </div>

          <div className="flex items-center gap-1 p-1 bg-muted rounded-xl">
            <button
              type="button"
              onClick={() => setTrendMetric('revenue')}
              className={`px-3 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                trendMetric === 'revenue' ? 'bg-surface text-text shadow-xs' : 'text-text-muted hover:text-text'
              }`}
            >
              Ventas ($ COP)
            </button>
            <button
              type="button"
              onClick={() => setTrendMetric('orders')}
              className={`px-3 py-1 text-xs font-bold rounded-lg transition cursor-pointer ${
                trendMetric === 'orders' ? 'bg-surface text-text shadow-xs' : 'text-text-muted hover:text-text'
              }`}
            >
              Pedidos (#)
            </button>
          </div>
        </div>

        {data?.trend && data.trend.length > 0 ? (
          <TrendAreaChart
            data={data.trend}
            isComparing={isComparing}
            metricType={trendMetric}
          />
        ) : (
          <div className="h-64 flex items-center justify-center text-xs text-text-muted">
            Cargando evolución temporal...
          </div>
        )}
      </div>

      {/* ── 6. Gráficas Circulares (Donas): Canales y Métodos de Pago ── */}
      <div className="grid gap-5 md:grid-cols-2">
        {/* Canales de Venta */}
        <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs">
          <h2 className="text-base font-bold text-text">Ventas por Canal de Entrega</h2>
          <p className="text-xs text-text-muted mb-2">
            Participación de Mesa (Salón), Para Llevar y Domicilios
          </p>
          <DonutChart
            data={
              data?.by_channel.map((c) => ({
                name: c.name,
                value: c.revenue,
                pct: c.pct,
                count: c.orders,
              })) || []
            }
            centerLabel="Canales"
            totalFormatted={kpis ? formatCOPShort(kpis.revenue.value) : undefined}
          />
        </div>

        {/* Métodos de Pago */}
        <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs">
          <h2 className="text-base font-bold text-text">Distribución por Método de Pago</h2>
          <p className="text-xs text-text-muted mb-2">
            Efectivo, Nequi, Tarjetas, Daviplata y transferencias
          </p>
          <DonutChart
            data={
              data?.by_payment.map((p) => ({
                name: p.name,
                value: p.amount,
                pct: p.pct,
                count: p.count,
              })) || []
            }
            centerLabel="Métodos"
            totalFormatted={kpis ? formatCOPShort(kpis.revenue.value) : undefined}
          />
        </div>
      </div>

      {/* ── 7. Top Productos & Ciclo de Vida del Pedido ── */}
      <div className="grid gap-5 lg:grid-cols-3">
        {/* Top 10 Productos (2 columnas) */}
        <div className="lg:col-span-2 rounded-2xl border border-border bg-surface p-5 shadow-xs">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div>
              <h2 className="text-base font-bold text-text">Top 10 Productos Más Vendidos</h2>
              <p className="text-xs text-text-muted">
                Volumen vendido, facturación total y margen de ganancia estimado por receta
              </p>
            </div>
            <Link
              to="/reports?tab=menu-engineering"
              className="inline-flex items-center gap-1 text-xs font-bold text-emerald-600 hover:text-emerald-700"
            >
              Matriz BCG <ArrowRight size={13} />
            </Link>
          </div>
          <TopProductsTable products={data?.top_products || []} />
        </div>

        {/* Ciclo del Pedido (1 columna) */}
        <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs">
          <h2 className="text-base font-bold text-text">Ciclo y Conversión de Pedidos</h2>
          <p className="text-xs text-text-muted mb-3">
            Avance de las órdenes desde la toma hasta el cobro y entrega
          </p>
          <OrderCycleFunnel
            stages={data?.order_lifecycle.stages || []}
            totalCreated={data?.order_lifecycle.total_created || 0}
          />
        </div>
      </div>

      {/* ── 8. Horas Pico (Mapa de Calor) ── */}
      <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs">
        <div className="mb-3">
          <h2 className="text-base font-bold text-text">Mapa de Calor: Días y Horas Pico</h2>
          <p className="text-xs text-text-muted">
            Identifica las franjas horarias de mayor flujo para optimizar turnos de personal y preparación
          </p>
        </div>
        {data?.heatmap ? (
          <HourHeatmap data={data.heatmap} />
        ) : (
          <div className="h-44 flex items-center justify-center text-xs text-text-muted">
            Cargando mapa de calor...
          </div>
        )}
      </div>

      {/* ── 9. Integración Unificada con Reportes Detallados (/reports) ── */}
      <div className="rounded-2xl border border-border bg-surface p-5 shadow-xs">
        <div className="mb-4">
          <h2 className="text-base font-bold text-text">Informes Especializados & Auditoría</h2>
          <p className="text-xs text-text-muted">
            Acceso directo a análisis profundos de personal, rentabilidad de recetas y fiscalidad
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Link
            to="/reports?tab=menu-engineering"
            className="group p-4 rounded-xl border border-border bg-surface hover:border-emerald-500/40 hover:shadow-xs transition"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600 dark:bg-amber-950">
                ⭐
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm text-text group-hover:text-emerald-600 transition">Ingeniería de Menú</p>
                <p className="text-xs text-text-muted truncate">Matriz BCG (Rentabilidad vs Volumen)</p>
              </div>
            </div>
          </Link>

          <Link
            to="/reports?tab=waiters"
            className="group p-4 rounded-xl border border-border bg-surface hover:border-emerald-500/40 hover:shadow-xs transition"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600 dark:bg-sky-950">
                🥇
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm text-text group-hover:text-emerald-600 transition">Rendimiento de Meseros</p>
                <p className="text-xs text-text-muted truncate">Podio del staff y promedio por ticket</p>
              </div>
            </div>
          </Link>

          <Link
            to="/reports?tab=tips"
            className="group p-4 rounded-xl border border-border bg-surface hover:border-emerald-500/40 hover:shadow-xs transition"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-teal-50 text-teal-600 dark:bg-teal-950">
                💵
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm text-text group-hover:text-emerald-600 transition">Control de Propinas</p>
                <p className="text-xs text-text-muted truncate">Liquidación equitativa y reparto</p>
              </div>
            </div>
          </Link>

          <Link
            to="/reports"
            className="group p-4 rounded-xl border border-border bg-surface hover:border-emerald-500/40 hover:shadow-xs transition"
          >
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950">
                <FileSpreadsheet size={18} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm text-text group-hover:text-emerald-600 transition">Exportación Contable</p>
                <p className="text-xs text-text-muted truncate">Descargar ventas en CSV / Excel</p>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* ── 10. Modo Avanzado (Explorador Técnico Opcional) ── */}
      <div className="rounded-2xl border border-border/80 bg-muted/30 p-4">
        <button
          type="button"
          onClick={() => setShowAdvancedExplorer(!showAdvancedExplorer)}
          className="flex w-full items-center justify-between text-xs font-bold text-text-muted hover:text-text cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <Activity size={14} className="text-primary" />
            Modo Avanzado: Consultas específicas y documentación API
          </span>
          {showAdvancedExplorer ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>

        {showAdvancedExplorer && (
          <div className="mt-4 pt-4 border-t border-border grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
            <Link
              to="/kpi/timeseries"
              className="p-3 rounded-xl border border-border bg-surface text-xs hover:border-emerald-500/40 transition"
            >
              <p className="font-bold text-text">Series de tiempo personalizadas</p>
              <p className="text-text-muted text-[11px]">Agrupaciones ad-hoc por fecha e intervalos</p>
            </Link>

            <Link
              to="/kpi/anomalies"
              className="p-3 rounded-xl border border-border bg-surface text-xs hover:border-emerald-500/40 transition"
            >
              <p className="font-bold text-text">Detección matemática de anomalías</p>
              <p className="text-text-muted text-[11px]">Identificación de desvíos estadísticos (&gt; 2σ)</p>
            </Link>

            <Link
              to="/kpi/docs"
              className="p-3 rounded-xl border border-border bg-surface text-xs hover:border-emerald-500/40 transition"
            >
              <p className="font-bold text-text">Documentación técnica de API</p>
              <p className="text-text-muted text-[11px]">Endpoints REST de analítica y esquemas</p>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
